'use client';

import React, { useEffect, useRef, useState } from 'react';

import classes from './shop-detail-messages.module.scss';
import { useDictionary } from '@/lib/context/locale-provider';
import {
  convertMonthAndDayToString,
  convertTimeToString,
  isToday,
} from '@/lib/convert-datetime';
import { useSession } from 'next-auth/react';
import { useDispatch } from 'react-redux';
import { setErrorMessage } from '@/slice/error';

function ShopDetailMessages({ shopId }: { shopId: string }) {
  const dict = useDictionary();
  const dispatch = useDispatch();
  const session = useSession();
  const textRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [textareaHeight, setTextareaHeight] = React.useState('36px');
  const [messages, setMessages] = useState<
    {
      text: string;
      userId: string;
      createdAt: string;
      userNickname: string;
    }[]
  >([]);
  const [skip, setSkip] = useState(0);

  const handleTextArea = (e: any) => {
    setTextareaHeight(e.target.scrollHeight);
  };

  const sendMessage = async () => {
    if (!session.data?.user.userId) {
      dispatch(setErrorMessage(dict!.error.pleaseLogin));
    }
    const text = textRef.current?.value.trim() || '';
    if (textRef.current) {
      textRef.current.value = '';
      setTextareaHeight('36px');
    }

    if (text.length > 0) {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/shops/cheering/messages/${shopId}`,
        {
          method: 'POST',
          body: JSON.stringify({ text }),
        },
      );

      if (res.ok) {
        const result = await res.json();
        if (result.status !== 201) {
          dispatch(setErrorMessage(dict!.error.pleaseLogin));
        }
      }
    }
  };

  // 첫 메시지 목록 불러오기
  useEffect(() => {
    async function getMessages() {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/shops/cheering/messages/${shopId}?skip=${skip}`,
      );
      if (res.ok) {
        const messages = await res.json();
        setMessages(messages);
      }
    }

    getMessages();
  }, [shopId, skip]);

  // SSE를 통한 실시간 메시지 업데이트
  useEffect(() => {
    const eventSource = new EventSource(
      `${process.env.NEXT_PUBLIC_API_URL}/api/shops/cheering/messages/${shopId}/sse`,
    );
    eventSource.onmessage = event => {
      const newMessage = JSON.parse(event.data);
      setMessages(prev => [...prev, newMessage]);
    };

    eventSource.onerror = () => {
      eventSource.close();
    };

    return () => eventSource.close();
  }, [shopId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div className={classes.detailCheeringGroup}>
      <div className={classes.chatGroup}>
        <div className={classes.chatList} ref={scrollRef}>
          <ul>
            {messages.length > 0 &&
              messages.map((message, index) => {
                let useNickname = true;
                let myMessage = false;
                if (index > 0) {
                  const currentNickname = message.userNickname;
                  const prevNickname = messages[index - 1].userNickname;

                  if (prevNickname === currentNickname) {
                    useNickname = false;
                  }
                }

                if (message.userId === session.data?.user.userId) {
                  myMessage = true;
                  useNickname = false;
                }

                return (
                  <li
                    key={message.createdAt.replace(/[.:]/g, '')}
                    className={myMessage ? classes.my : undefined}
                  >
                    {useNickname && (
                      <div className={classes.writerBox}>
                        {message.userNickname}
                      </div>
                    )}
                    <div className={classes.msgList}>
                      <div className={classes.msgItem}>
                        <div className={classes.msgTxt}>{message.text}</div>
                        <div className={classes.optionBox}>
                          <span className={classes.timeTxt}>
                            {isToday(message.createdAt)
                              ? convertTimeToString(message.createdAt)
                              : convertMonthAndDayToString(message.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
        <div className={classes.chatInput}>
          <div className={classes.inputBox}>
            <textarea
              ref={textRef}
              style={{ height: textareaHeight }}
              placeholder={dict!.placeholder.message}
              onKeyDown={handleTextArea}
              onKeyUp={handleTextArea}
            ></textarea>
          </div>
          <button
            type="button"
            className={classes.sendBtn}
            onClick={sendMessage}
          ></button>
        </div>
      </div>
    </div>
  );
}

export default ShopDetailMessages;
